import { z } from 'zod';
import { CREATED_RANGE_SHAPE, PageQuerySchema, UuidSchema } from './common';
import { ChannelKindSchema, RequestStatusSchema, RequestTypeSchema } from './enums';
import { EntityIntegrationSchema } from './integration';
import { RequestStatusCountsSchema, RequestTypeCountsSchema } from './metrics';

/**
 * §10 ТЗ. Обращения администратора заказчика.
 *
 * Один механизм на три типа: отдельных форм и отдельной маршрутизации
 * в базовом этапе нет (§10.1), поэтому и контракт один.
 */

export const RequestSummarySchema = z.object({
  id: UuidSchema,
  /** 'CR-2026-000123' — единственный идентификатор, который видит заявитель (§10.3). */
  number: z.string(),
  type: RequestTypeSchema,
  projectId: UuidSchema,
  projectName: z.string(),
  personName: z.string(),
  channel: ChannelKindSchema,
  status: RequestStatusSchema,
  /** §6.1: снимок ответственного на момент обращения, а не текущий менеджер. */
  managerSnapshotName: z.string().nullable(),
  bitrixTaskId: z.number().int().nullable(),
  createdAt: z.string().datetime(),
  closedAt: z.string().datetime().nullable(),
});
export type RequestSummary = z.infer<typeof RequestSummarySchema>;

export const RequestDetailSchema = RequestSummarySchema.extend({
  /** §10.2: исходный текст заявителя без смыслового изменения. */
  text: z.string(),
  /** Обрезан по технической длине — сотрудник должен видеть, что текст неполон. */
  textTruncated: z.boolean(),
  sourceMessageId: z.string().nullable(),
  projectAdminId: UuidSchema,
  externalKey: z.string(),
  /** Время в карточке показывается в часовом поясе объекта (§10.2). */
  projectTimezone: z.string(),
  /** блок «Интеграция»: состояние команды CREATE_TASK (§13, сценарий 34) */
  integration: EntityIntegrationSchema.nullable(),
});
export type RequestDetail = z.infer<typeof RequestDetailSchema>;

/**
 * §13 ТЗ. Сводка раздела «Обращения»: те же разрезы, что на дашборде, но
 * по отбору таблицы. Статусы считаются без фильтра статуса — плитка обязана
 * оставаться переключателем, а не показывать ноль по всем соседям.
 */
export const RequestsSummarySchema = z.object({
  generatedAt: z.string().datetime(),
  total: z.number().int().min(0),
  open: z.number().int().min(0),
  byStatus: z.array(RequestStatusCountsSchema).length(5),
  byType: z.array(RequestTypeCountsSchema).length(3),
});
export type RequestsSummary = z.infer<typeof RequestsSummarySchema>;

export const ListRequestsQuerySchema = PageQuerySchema.extend({
  ...CREATED_RANGE_SHAPE,
  projectId: UuidSchema.optional(),
  type: RequestTypeSchema.optional(),
  status: RequestStatusSchema.optional(),
  /** §13 «найти по номеру», §14 «из показателя — к карточке»: точный номер CR-… */
  number: z.string().trim().min(1).max(32).optional(),
});
export type ListRequestsQuery = z.infer<typeof ListRequestsQuerySchema>;

/**
 * §5.1: Новое → Принято → В работе → Закрыто, из работы — Отклонено.
 * `NEW` в список не входит: вернуть обращение в начало нельзя.
 */
export const RequestStatusRequestSchema = z.object({
  status: z.enum(['ACCEPTED', 'IN_PROGRESS', 'CLOSED', 'REJECTED']),
});
export type RequestStatusRequest = z.infer<typeof RequestStatusRequestSchema>;
